<template>
    <v-sheet>
        <v-fade-transition>
            <v-overlay v-show="!recruitment" color="white" absolute>
                <v-progress-circular indeterminate width="5" size="100" color="primary" />
            </v-overlay>
        </v-fade-transition>

        <recruitment-view-company v-bind="recruitment">
            <template #actions v-if="!isPreview">
                <v-row class="row--small" justify="end">
                    <v-col cols="auto">
                        <v-btn icon text outlined color="grey-c" class="like-btn" @click="copy">
                            <v-icon color="grey-9">mdi-link-variant</v-icon>
                        </v-btn>
                    </v-col>
                    <v-col cols="auto">
                        <like-btn :params="{ _recruitment, type: USER_LIKE_TYPES.RECRUITMENT.value }">
                            <template #activator="{ active, toggle }">
                                <v-btn icon text outlined :color="active ? 'yellow darken-1' : 'grey-c'" class="like-btn" @click="toggle">
                                    <v-icon :color="active ? undefined : 'grey-9'">{{ active ? "mdi-star" : "mdi-star-outline" }}</v-icon>
                                </v-btn>
                            </template>
                        </like-btn>
                    </v-col>
                    <v-col cols="auto" v-if="isUserWriter">
                        <recruitment-form :value="recruitment" @input="$emit('init')">
                            <template #activator="{ attrs, on }">
                                <v-btn color="primary" large class="px-lg-24" v-bind="attrs" v-on="on">수정하기</v-btn>
                            </template>
                        </recruitment-form>
                    </v-col>
                    <v-col cols="auto" v-else>
                        <application-form-dialog v-bind="{ _recruitment }" :recruitmentSubject="recruitment?.subject">
                            <template #activator="{ attrs, on }">
                                <v-btn color="primary" large class="px-lg-24" v-bind="attrs" v-on="on">지원하기</v-btn>
                            </template>
                        </application-form-dialog>
                    </v-col>
                </v-row>
            </template>
        </recruitment-view-company>

        <div class="page-section">
            <h3 class="tit">{{ recruitment?.subject }}</h3>

            <v-divider class="border-2 primary my-16 my-lg-20" />

            <!-- 직종 -->
            <recruitment-view-employment :employment="recruitment?.employment" class="mb-8 mb-lg-16" />

            <v-chip-group v-if="recruitment?.hashTags?.length" class="ma-n2">
                <v-chip v-for="hashTag in recruitment?.hashTags" :key="hashTag" disabled large outlined label color="grey-d" class="grey-9--text ma-2">#{{ hashTag }}</v-chip>
            </v-chip-group>

            <!-- 공고내용 -->
            <div v-html="recruitment?.content" class="mb-20 mb-lg-40 mt-16 mt-lg-20 recruitment-content" />

            <!-- 첨부파일 -->
            <recruitment-view-attachments v-bind="{ recruitment }" class="mb-20 mb-lg-40" />

            <vertical-table v-bind="{ items }" :itemsPerRow="2" class="mb-20 mb-lg-40">
                <template #접수기간>
                    <recruitment-view-application v-bind="{ application }" />
                </template>
            </vertical-table>

            <v-responsive :aspect-ratio="16 / 9" class="mt-16 mt-lg-20" v-if="recruitment?.employment?.place?.geolocation">
                <recruitment-view-map :place="recruitment?.employment?.place" />
            </v-responsive>
            <div class="v-btn-group">
                <template v-if="isUserWriter">
                    <recruitment-form :value="recruitment" @input="$emit('init')">
                        <template #activator="{ attrs, on }">
                            <v-btn v-bind="attrs" v-on="on" color="primary" class="min-w-140px min-w-lg-160px"><span>수정하기</span></v-btn>
                        </template>
                    </recruitment-form>
                </template>
                <template v-else>
                    <application-form-dialog v-bind="{ _recruitment }" :recruitmentSubject="recruitment?.subject">
                        <template #activator="{ attrs, on }">
                            <v-btn v-bind="attrs" v-on="on" color="primary" class="min-w-140px min-w-lg-160px"><span>지원하기</span></v-btn>
                        </template>
                    </application-form-dialog>
                </template>
            </div>
        </div>
    </v-sheet>
</template>

<script>
import { EDUCATION_LEVELS, EMPLOYMENT_TYPES, GENDERS, initRecruitment, USER_LIKE_TYPES, WAGE_TYPES, WORK_EXPERIENCES } from "@/assets/variables";

import LikeBtn from "@/components/client/like/like-btn.vue";
import VerticalTable from "@/components/dumb/vertical-table.vue";
import RecruitmentForm from "@/components/dashboard/company/recruitment/form/recruitment-form.vue";
import ApplicationFormDialog from "@/components/client/recruitments/form/application-form-dialog.vue";
import RecruitmentViewMap from "./recruitment-view-map.vue";
import RecruitmentViewCompany from "./recruitment-view-company.vue";
import RecruitmentViewEmployment from "./recruitment-view-employment.vue";
import RecruitmentViewAttachments from "./recruitment-view-attachments.vue";
import RecruitmentViewApplication from "./recruitment-view-application.vue";

const headerProps = { class: "min-w-160px" };

export default {
    components: {
        LikeBtn,
        VerticalTable,
        RecruitmentForm,
        ApplicationFormDialog,
        RecruitmentViewMap,
        RecruitmentViewCompany,
        RecruitmentViewEmployment,
        RecruitmentViewAttachments,
        RecruitmentViewApplication,
    },
    props: {
        recruitment: { type: Object, default: initRecruitment },
        isPreview: { type: Boolean, default: false },
    },
    data: () => ({
        USER_LIKE_TYPES,
    }),
    computed: {
        isUserWriter() {
            return this.recruitment?._company == this.$store.state.payload?._user;
        },
        _recruitment() {
            return this.recruitment?._id;
        },
        application() {
            return this.recruitment?.application || {};
        },
        employment() {
            return this.recruitment?.employment || {};
        },
        qualification() {
            return this.recruitment?.qualification || {};
        },
        recruiter() {
            return this.recruitment?.recruiter || {};
        },

        employmentWage() {
            let { type, value__min, value__max } = this.employment?.wage || {};

            let text = WAGE_TYPES[type]?.text || "";
            if (value__min || value__max) {
                if (value__min == value__max) text += ` ${value__min?.format?.() || ""}원`;
                else {
                    if (value__min) text += ` ${value__min?.format?.() || ""}원`;
                    text += " ~";
                    if (value__max) text += ` ${value__max?.format?.() || ""}원`;
                }
            }
            return text.trim();
        },

        employmentType() {
            return (this.employment?.types || [])
                .map((item) => EMPLOYMENT_TYPES[item]?.text)
                .filter((item) => item)
                .join(" · ");
        },

        employmentPlace() {
            const { isAbroad, address1, address2 } = this.employment?.place || {};

            let text = "";
            if (isAbroad) text = `[해외] ${address2}`;
            else text = `${address1 || ""} ${address2 || ""}`;
            return text.trim();
        },

        workExperience() {
            const { isIrrelevant, years, months, items: [...items] = [] } = this.qualification?.workExperience || {};

            if (isIrrelevant) return "경력무관";

            const texts = items.map((item) => WORK_EXPERIENCES[item]?.text);
            if (years || months) {
                let index = texts.findIndex((item) => item == WORK_EXPERIENCES.EXPERIENCED.text);
                if (0 <= index) {
                    texts[index] += " (";
                    if (years) texts[index] += `${years}년 `;
                    if (months) texts[index] += `${months}개월 `;
                    texts[index] += "이상)";
                }
            }
            return texts.join(" · ");
        },

        educationLevel() {
            const { isIrrelevant, includesToBe, items = [] } = this.qualification?.educationLevel || {};

            if (isIrrelevant) return "학력무관";

            const texts = items.map((item) => EDUCATION_LEVELS[item]?.text);
            return texts.join(" / ") + (includesToBe ? "\n(졸업예정자 가능)" : "");
        },

        qualificationGender() {
            if (this.qualification?.gender?.isIrrelevant) return "성별무관";
            return GENDERS[this.qualification?.gender.value]?.text;
        },

        qualificationAge() {
            let { isIrrelevant, value__min, value__max } = this.qualification?.age || {};
            if (isIrrelevant) return "연령무관";

            let text = "";

            if (value__min || value__max) {
                if (value__min == value__max) text += `${value__min?.format?.() || ""}세`;
                else {
                    if (value__min) text += `${value__min?.format?.() || ""}세`;
                    text += " ~";
                    if (value__max) text += ` ${value__max?.format?.() || ""}세`;
                }
            }
            return text;
        },

        recruiterText() {
            const { name, phone, email, hidesPhone, hidesEmail } = this.recruiter || {};

            let text = "";
            if (name) text += name;
            if (phone && !hidesPhone) text += (text ? "\n" : "") + phone;
            if (email && !hidesEmail) text += (text ? "\n" : "") + email;
            return text;
        },

        items() {
            return [
                { term: "경력구분", data: this.workExperience, headerProps },
                { term: "근무지주소", data: this.employmentPlace, headerProps },
                [
                    { term: "고용형태", data: this.employmentType, headerProps },
                    { term: "급여조건", data: this.employmentWage, headerProps },
                    { term: "접수기간", headerProps },
                ],
                { term: "채용담당자", data: this.recruiterText, headerProps },
                // { term: "학력", data: this.educationLevel, headerProps },
                // { term: "성별제한", data: this.qualificationGender, headerProps },
                // { term: "연령제한", data: this.qualificationAge, headerProps },
            ];
        },
    },
    methods: {
        copy() {
            const url = `${window.location.origin}/recruitments/${this.recruitment._id}`;
            navigator.clipboard.writeText(url).then(() => alert("주소가 복사되었습니다"));
        },
        share() {
            const { company, subject } = this.recruitment || {};
            const title = `[마이스펙] ${company?.name} - ${subject}`;

            const text = [this.employmentType, this.workExperience, this.employmentWage].filter((item) => item).join(" / ");
            const url = `${window.location.origin}/recruitments/${this.recruitment._id}`;

            navigator.share({ title, text, url });
        },
    },
};
</script>

<style lang="scss" scoped>
.like-btn {
    width: 44px;
    height: 44px;
    border-radius: 6px;
    .v-icon {
        font-size: 20px;
    }
}
::v-deep {
    .v-chip.v-size--large {
        font-weight: 500;
    }
}
@media (min-width: 576px) {
}
@media (min-width: 768px) {
}
@media (min-width: 1024px) {
    .like-btn {
        width: 56px;
        height: 56px;
        .v-icon {
            font-size: 26px;
        }
    }
    ::v-deep {
        .v-chip.v-size--large {
            padding: 0 14px;
        }
    }
}
@media (min-width: 1200px) {
}
</style>
